/**
 * Régua da pessoa: zonas de uso, alcance, cabeça e operação.
 *
 * A régua de tipologia (`typology.ts`) responde "quanto vão entre um suspenso e
 * o que está abaixo". Ela não responde "a que altura do piso isto precisa
 * estar para ser usado", "onde a folha aberta vai parar" nem "para que serve a
 * superfície de baixo". Este módulo materializa essas três perguntas como
 * dados, na mesma forma de tabela: valores consolidados e conservadores de
 * ergonomia e prática de interiores, comentados e editáveis, para que
 * planejador, ferramentas, validador do plano e revisor usem exatamente a
 * mesma referência em vez de depender da memória do modelo.
 *
 * Nada aqui é específico de um ambiente ou de um móvel. A atividade na
 * superfície (trabalhar em pé, apoiar, expor, sentar, dormir, circular) é o
 * que escolhe a faixa — o mesmo módulo superior sobre a mesma base fica numa
 * cota numa cozinha e em outra numa sala.
 */

import type { InstallationTypology } from "./typology";

export type RoomFunction =
  | "kitchen"
  | "dining"
  | "living"
  | "bedroom"
  | "bathroom"
  | "office"
  | "laundry"
  | "leisure"
  | "service"
  | "circulation"
  | "undefined";

export type SurfaceActivity =
  | "work_standing"
  | "work_seated"
  | "support"
  | "display"
  | "seating"
  | "sleeping"
  | "appliance"
  | "circulation"
  | "unknown";

export type ActivityConfidence = "declared" | "high" | "medium" | "low";

export interface ReachZones {
  /** Faixa em que uso frequente em pé é confortável (sem se curvar nem esticar). */
  primeMm: [number, number];
  /** Faixa aceitável para uso regular em pé. */
  acceptableMm: [number, number];
  /** Acima disto o uso é esporádico ou exige escada. */
  occasionalAboveMm: number;
  /** Topo de elemento de guarda que um adulto ainda alcança sem escada (prateleira de cima). */
  storageTopComfortMm: number;
  /** Topo máximo recomendado para guarda de uso regular. */
  storageTopMaxMm: number;
  /** Zona da cabeça de uma pessoa em pé: nenhum envelope de operação ou quina fixa deve invadi-la onde ela opera ou passa. */
  headZoneMm: [number, number];
  /** Vão livre mínimo sob elemento fixo em área de circulação. */
  clearUnderFixedInCirculationMm: number;
  /** Altura dos olhos em pé (centro visual de espelhos, quadros, telas). */
  eyeLevelStandingMm: [number, number];
  /** Altura dos olhos sentado. */
  eyeLevelSeatedMm: [number, number];
}

export interface UserProfile {
  id: "adult-standard";
  label: string;
  statureMm: [number, number];
  reach: ReachZones;
}

/**
 * Perfil padrão: adulto entre 1,60 e 1,80 m. Valores conservadores; quando o
 * perfil do cômodo declarar outro público (crianças, cadeirantes, idosos), o
 * perfil é substituído, não "ajustado" na cabeça do modelo.
 */
export const STANDING_ADULT: UserProfile = {
  id: "adult-standard",
  label: "adulto de pé (1,60–1,80 m)",
  statureMm: [1_600, 1_800],
  reach: {
    primeMm: [700, 1_600],
    acceptableMm: [400, 1_800],
    occasionalAboveMm: 1_800,
    storageTopComfortMm: 2_100,
    storageTopMaxMm: 2_300,
    headZoneMm: [1_500, 1_950],
    clearUnderFixedInCirculationMm: 2_050,
    eyeLevelStandingMm: [1_500, 1_650],
    eyeLevelSeatedMm: [1_100, 1_250],
  },
};

export interface ActivityBand {
  activity: SurfaceActivity;
  label: string;
  /** Altura típica do topo da superfície para esta atividade, quando a atividade define uma. */
  surfaceTopMm: [number, number] | null;
  /** Face inferior de um elemento de guarda suspenso sobre essa superfície (mm do piso). null = sem prática fixa; vale a linha existente ou a folga. */
  suspendedBottomMm: [number, number] | null;
  /** Vão livre entre a superfície e a face inferior do suspenso de guarda. */
  clearanceOverSurfaceMm: [number, number] | null;
  /** Se a face inferior pode seguir uma linha existente da parede em vez da faixa (apoio, exposição) ou se a faixa é de uso (trabalho). */
  lineMayOverride: boolean;
  summary: string;
}

/**
 * Faixas por atividade na superfície de baixo. A régua de tipologia continua
 * sendo a autoridade do vão mínimo; esta tabela diz onde a face inferior fica
 * para a pessoa usar a superfície e o que está guardado.
 */
export const ACTIVITY_BANDS: ReadonlyArray<ActivityBand> = [
  {
    activity: "work_standing",
    label: "trabalho em pé (preparo, lavagem, bancada de serviço)",
    surfaceTopMm: [850, 950],
    suspendedBottomMm: [1_350, 1_550],
    clearanceOverSurfaceMm: [450, 650],
    lineMayOverride: false,
    summary: "Superfície de trabalho em pé entre 850 e 950 mm; suspenso de guarda com face inferior entre 1.350 e 1.550 mm (vão de 450 a 650 mm) para operar sem bater a cabeça e alcançar a primeira prateleira.",
  },
  {
    activity: "work_seated",
    label: "trabalho sentado (mesa, escrivaninha, penteadeira)",
    surfaceTopMm: [700, 780],
    suspendedBottomMm: [1_200, 1_500],
    clearanceOverSurfaceMm: [450, 750],
    lineMayOverride: true,
    summary: "Superfície de trabalho sentado entre 700 e 780 mm; prateleira ou suspenso com face inferior entre 1.200 e 1.500 mm (450 a 750 mm acima do tampo) para não interferir com tela, luminária e cabeça de quem está sentado.",
  },
  {
    activity: "support",
    label: "apoio e guarda (aparador, rack, cômoda, balcão de apoio)",
    surfaceTopMm: [600, 950],
    suspendedBottomMm: [1_150, 1_650],
    clearanceOverSurfaceMm: [400, 900],
    lineMayOverride: true,
    summary: "Superfície de apoio ou guarda; suspenso com vão de 400 a 900 mm acima dela, face inferior entre 1.150 e 1.650 mm — ou alinhado a uma linha que a parede já tenha (topo de outro suspenso, verga, teto), porque aqui a composição pesa mais que o alcance frequente.",
  },
  {
    activity: "display",
    label: "exposição (aparador decorativo, nicho, bancada de passagem)",
    surfaceTopMm: [600, 1_000],
    suspendedBottomMm: [1_200, 1_700],
    clearanceOverSurfaceMm: [300, 900],
    lineMayOverride: true,
    summary: "Superfície de exposição; o elemento acima (quadro, espelho, prateleira) responde à altura dos olhos e às linhas da parede, não ao alcance frequente.",
  },
  {
    activity: "seating",
    label: "assento (sofá, poltrona, banco, cama usada como assento)",
    surfaceTopMm: [400, 1_050],
    suspendedBottomMm: [1_250, 1_700],
    clearanceOverSurfaceMm: [250, 800],
    lineMayOverride: true,
    summary: "Sobre assento, a face inferior de um suspenso de guarda fica acima da cabeça de quem está sentado (≥ 1.250 mm); quadro ou espelho fica de 100 a 300 mm acima do encosto.",
  },
  {
    activity: "sleeping",
    label: "cama (cabeceira, criado)",
    surfaceTopMm: [400, 1_200],
    suspendedBottomMm: [1_250, 1_700],
    clearanceOverSurfaceMm: [250, 800],
    lineMayOverride: true,
    summary: "Sobre a cama ou a cabeceira, prateleira ou nicho com face inferior ≥ 1.250 mm para quem senta na cama; sobre criado-mudo, 300 a 600 mm acima do tampo.",
  },
  {
    activity: "appliance",
    label: "eletrodoméstico (geladeira, lavadora, forno)",
    surfaceTopMm: null,
    suspendedBottomMm: null,
    clearanceOverSurfaceMm: [30, 150],
    lineMayOverride: true,
    summary: "Sobre eletrodoméstico vale a folga de ventilação e retirada (50 a 150 mm, mínimo 30); a face inferior acompanha o topo do equipamento.",
  },
  {
    activity: "circulation",
    label: "circulação (nada embaixo, passagem de pessoas)",
    surfaceTopMm: null,
    suspendedBottomMm: [2_050, 2_400],
    clearanceOverSurfaceMm: null,
    lineMayOverride: true,
    summary: "Elemento fixo sobre circulação precisa deixar ≥ 2.050 mm livres do piso.",
  },
  {
    activity: "unknown",
    label: "atividade indefinida",
    surfaceTopMm: null,
    suspendedBottomMm: [1_150, 1_650],
    clearanceOverSurfaceMm: [350, 900],
    lineMayOverride: true,
    summary: "Sem função declarada nem inferível, vale a faixa larga de guarda (face inferior entre 1.150 e 1.650 mm) ou uma linha existente da parede, com a premissa registrada no plano.",
  },
];

export function activityBand(activity: SurfaceActivity): ActivityBand {
  return ACTIVITY_BANDS.find((band) => band.activity === activity) ?? ACTIVITY_BANDS[ACTIVITY_BANDS.length - 1]!;
}

export const ACTIVITY_LABELS: Record<SurfaceActivity, string> = Object.fromEntries(
  ACTIVITY_BANDS.map((band) => [band.activity, band.label]),
) as Record<SurfaceActivity, string>;

export const ROOM_FUNCTION_LABELS: Record<RoomFunction, string> = {
  kitchen: "cozinha",
  dining: "sala de jantar",
  living: "sala de estar",
  bedroom: "dormitório",
  bathroom: "banheiro / lavabo",
  office: "escritório / estudo",
  laundry: "área de serviço / lavanderia",
  leisure: "área de lazer / gourmet",
  service: "área técnica / depósito",
  circulation: "circulação / hall",
  undefined: "função não definida",
};

/**
 * Atividade provável na superfície de um elemento de apoio, dada a tipologia
 * dele e a função do cômodo. Confiança baixa quando só a proporção ou a
 * função indefinida sustentam a inferência — o planejador deve então declarar
 * a premissa.
 */
export function inferSurfaceActivity(input: {
  lowerTypology: InstallationTypology;
  roomFunction: RoomFunction;
  surfaceTopMm?: number | null;
  typologyConfidence?: "high" | "medium" | "low";
}): { activity: SurfaceActivity; confidence: ActivityConfidence; basis: string } {
  const { lowerTypology, roomFunction } = input;
  const top = input.surfaceTopMm ?? null;
  const typologyConfidence = input.typologyConfidence ?? "medium";
  const weak = typologyConfidence === "low";
  switch (lowerTypology) {
    case "countertop":
      if (roomFunction === "kitchen" || roomFunction === "laundry" || roomFunction === "leisure" || roomFunction === "bathroom") {
        return { activity: "work_standing", confidence: "high", basis: `bancada em ${ROOM_FUNCTION_LABELS[roomFunction]}` };
      }
      return { activity: top !== null && top >= 820 ? "work_standing" : "support", confidence: "medium", basis: "bancada fora de ambiente de trabalho conhecido" };
    case "base_cabinet":
      if (roomFunction === "kitchen" || roomFunction === "laundry" || roomFunction === "leisure" || roomFunction === "bathroom") {
        return { activity: "work_standing", confidence: "high", basis: `balcão em ${ROOM_FUNCTION_LABELS[roomFunction]}` };
      }
      return { activity: top !== null && top >= 820 ? "work_standing" : "support", confidence: weak ? "low" : "medium", basis: "balcão ou base sem função de ambiente declarada" };
    case "low_furniture":
      if (roomFunction === "kitchen" && top !== null && top >= 820) {
        return { activity: "work_standing", confidence: "medium", basis: "móvel baixo na altura de bancada em cozinha" };
      }
      return { activity: "support", confidence: weak ? "low" : roomFunction === "undefined" ? "medium" : "high", basis: weak ? "móvel baixo inferido por proporção" : "móvel baixo de apoio" };
    case "table":
      return { activity: "work_seated", confidence: "high", basis: "mesa ou escrivaninha" };
    case "seating":
      return { activity: "seating", confidence: "high", basis: "assento" };
    case "bed":
      return { activity: "sleeping", confidence: "high", basis: "cama" };
    case "appliance":
      return { activity: "appliance", confidence: "high", basis: "eletrodoméstico" };
    case "tall_unit":
      return { activity: "unknown", confidence: "low", basis: "volume alto; não é superfície de uso" };
    default:
      return { activity: "unknown", confidence: "low", basis: "tipologia do apoio não identificada" };
  }
}

export interface VerticalPlacementBand {
  /** Faixa de face inferior do elemento suspenso (mm do piso). */
  bottomHeightMm: [number, number] | null;
  /** Vão livre sobre a superfície (mm). */
  clearanceMm: [number, number] | null;
  /** Se uma linha existente da parede pode substituir a faixa. */
  lineMayOverride: boolean;
  activity: SurfaceActivity;
  basis: string[];
}

/**
 * Combina a atividade (o que a pessoa faz na superfície) com a régua do par
 * (o que a tipologia exige). O mínimo do par nunca é relaxado; a faixa da
 * atividade decide onde a face inferior fica.
 */
export function verticalPlacementBand(input: {
  activity: SurfaceActivity;
  activityConfidence: ActivityConfidence;
  pairBottomHeightMm: [number, number] | null;
  pairClearanceMm: [number, number] | null;
  pairMinimumMm: number | null;
  upperKind?: InstallationTypology;
}): VerticalPlacementBand {
  const band = activityBand(input.activity);
  const basis: string[] = [];
  const upperIsStorage = !input.upperKind || input.upperKind === "upper_cabinet" || input.upperKind === "shelf" || input.upperKind === "unknown";
  // Quadro, espelho, coifa, pendente e painel têm régua própria no par; a
  // atividade só importa para o que a pessoa alcança e guarda.
  const useActivity = upperIsStorage && input.activity !== "unknown" && input.activityConfidence !== "low";
  let bottom: [number, number] | null = null;
  let clearance: [number, number] | null = null;
  if (useActivity) {
    bottom = band.suspendedBottomMm;
    clearance = band.clearanceOverSurfaceMm;
    basis.push(`atividade na superfície: ${band.label}`);
  }
  if (!bottom) {
    bottom = input.pairBottomHeightMm ?? (upperIsStorage ? activityBand("unknown").suspendedBottomMm : null);
    if (input.pairBottomHeightMm) basis.push("faixa da régua de tipologia do par");
    else if (bottom) basis.push("faixa larga de guarda (atividade indefinida)");
  }
  if (!clearance) {
    clearance = input.pairClearanceMm;
    if (clearance) basis.push("vão típico da régua de tipologia do par");
  }
  if (input.pairMinimumMm !== null && clearance && clearance[0] < input.pairMinimumMm) {
    clearance = [input.pairMinimumMm, Math.max(input.pairMinimumMm, clearance[1])];
    basis.push(`vão mínimo do par (${input.pairMinimumMm} mm) prevalece`);
  }
  return {
    bottomHeightMm: bottom,
    clearanceMm: clearance,
    lineMayOverride: useActivity ? band.lineMayOverride : true,
    activity: useActivity ? input.activity : "unknown",
    basis,
  };
}

export type ReachStatus = "prime" | "acceptable" | "occasional" | "out_of_reach" | "too_low";

export interface ReachAssessment {
  status: ReachStatus;
  /** Parte do conteúdo (0–1) dentro da zona aceitável. */
  acceptableShare: number;
  message: string;
}

/**
 * Julga a faixa vertical do conteúdo de um elemento de guarda suspenso contra
 * as zonas de alcance. Uma guarda totalmente acima da zona aceitável é uso
 * esporádico: aceitável só como decisão declarada (maleiro, linha existente),
 * nunca como padrão silencioso.
 */
export function reachAssessment(input: {
  bottomMm: number;
  topMm: number;
  profile?: UserProfile;
}): ReachAssessment {
  const reach = (input.profile ?? STANDING_ADULT).reach;
  const bottom = Math.min(input.bottomMm, input.topMm);
  const top = Math.max(input.bottomMm, input.topMm);
  const height = Math.max(1, top - bottom);
  const overlap = Math.max(0, Math.min(top, reach.acceptableMm[1]) - Math.max(bottom, reach.acceptableMm[0]));
  const acceptableShare = Math.round((overlap / height) * 100) / 100;
  if (bottom >= reach.occasionalAboveMm) {
    return {
      status: "out_of_reach",
      acceptableShare,
      message: `Conteúdo entre ${Math.round(bottom)} e ${Math.round(top)} mm: todo acima da zona de alcance em pé (${reach.acceptableMm[0]}–${reach.acceptableMm[1]} mm); uso esporádico ou com escada — só como decisão declarada (guarda alta alinhada a uma linha existente ou ao teto).`,
    };
  }
  if (top <= reach.acceptableMm[0]) {
    return {
      status: "too_low",
      acceptableShare,
      message: `Conteúdo entre ${Math.round(bottom)} e ${Math.round(top)} mm: abaixo da zona de uso em pé (${reach.acceptableMm[0]} mm); exige agachar.`,
    };
  }
  const primeOverlap = Math.max(0, Math.min(top, reach.primeMm[1]) - Math.max(bottom, reach.primeMm[0]));
  if (primeOverlap / height >= 0.5) {
    return { status: "prime", acceptableShare, message: `Conteúdo entre ${Math.round(bottom)} e ${Math.round(top)} mm: na zona nobre de uso em pé (${reach.primeMm[0]}–${reach.primeMm[1]} mm).` };
  }
  if (acceptableShare >= 0.5) {
    return { status: "acceptable", acceptableShare, message: `Conteúdo entre ${Math.round(bottom)} e ${Math.round(top)} mm: na zona aceitável de uso em pé (${reach.acceptableMm[0]}–${reach.acceptableMm[1]} mm).` };
  }
  return {
    status: "occasional",
    acceptableShare,
    message: `Conteúdo entre ${Math.round(bottom)} e ${Math.round(top)} mm: em grande parte acima de ${reach.occasionalAboveMm} mm; uso esporádico — registre a premissa (guarda alta) ou alinhe a uma linha existente.`,
  };
}

export type OpeningKind = "top_hinged" | "side_hinged" | "drawer" | "sliding" | "lift_up" | "unknown";

export interface OperationEnvelopeAssessment {
  status: "clear" | "head_zone" | "unknown";
  message: string | null;
}

/**
 * Onde a folha aberta vai parar. Uma folha basculante (articulada no topo) ou
 * lift-up projeta-se para a frente na altura do topo do módulo; uma porta de
 * giro varre a frente na altura do módulo inteiro. Se o envelope cruzar a
 * zona de cabeça no espaço onde a pessoa opera — e não houver uma superfície
 * profunda que a mantenha afastada — é objeção de uso.
 */
export function operationEnvelopeAssessment(input: {
  opening: OpeningKind;
  bottomMm: number;
  topMm: number;
  /** Projeção horizontal do envelope aberto à frente do módulo (mm). */
  projectionMm: number;
  /** Profundidade da superfície abaixo que afasta a pessoa (bancada, base); 0 quando a pessoa encosta no módulo. */
  standoffMm: number;
  profile?: UserProfile;
}): OperationEnvelopeAssessment {
  const reach = (input.profile ?? STANDING_ADULT).reach;
  if (input.opening === "unknown") return { status: "unknown", message: null };
  const [headLow, headHigh] = reach.headZoneMm;
  // Com standoff maior que a projeção, a folha aberta não alcança a cabeça.
  const reachesPerson = input.projectionMm > input.standoffMm;
  let envelopeBand: [number, number];
  switch (input.opening) {
    case "top_hinged":
    case "lift_up":
      envelopeBand = [input.topMm - 60, input.topMm + 60];
      break;
    case "side_hinged":
      envelopeBand = [input.bottomMm, input.topMm];
      break;
    case "drawer":
    case "sliding":
    default:
      return { status: "clear", message: null };
  }
  const intersects = envelopeBand[1] >= headLow && envelopeBand[0] <= headHigh;
  if (intersects && reachesPerson) {
    return {
      status: "head_zone",
      message: `A folha aberta (${input.opening === "side_hinged" ? "porta de giro" : "basculante"}) ocupa a faixa ${Math.round(envelopeBand[0])}–${Math.round(envelopeBand[1])} mm, dentro da zona de cabeça (${headLow}–${headHigh} mm), com projeção de ${Math.round(input.projectionMm)} mm e apenas ${Math.round(input.standoffMm)} mm de afastamento: a pessoa opera com a folha na altura do rosto. Suba o módulo (topo acima de ${headHigh} mm), troque a abertura ou garanta afastamento.`,
    };
  }
  return { status: "clear", message: null };
}

/**
 * Tipo de abertura a partir da linguagem do produto ou das interações
 * declaradas. Conservador: só classifica quando há termo claro.
 */
export function inferOpeningKind(input: { name?: string | null; description?: string | null; interactionNames?: ReadonlyArray<string> }): OpeningKind {
  const text = [input.name ?? "", input.description ?? "", ...(input.interactionNames ?? [])]
    .join(" ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR");
  if (/\bbasc/.test(text)) return "top_hinged";
  if (/lift|elevat|articulad/.test(text)) return "lift_up";
  if (/gaveta|gavet/.test(text)) return "drawer";
  if (/correr|deslizant/.test(text)) return "sliding";
  if (/porta|folha|giro|abrir/.test(text)) return "side_hinged";
  return "unknown";
}
